// src/components/AssignMemberPhotos.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, collection, query, where, orderBy, getDocs, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { Container, Row, Col, Spinner, Alert, Button, Image, ListGroup, Breadcrumb, Figure, Card } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import './AssignMemberPhotos.css';

const DEFAULT_MEMBER_PHOTO_URL = '/Person.png';

function AssignMemberPhotos() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const { currentUser, loadingAuth } = useAuth();

    const [family, setFamily] = useState(null);
    const [members, setMembers] = useState([]);
    const [memberPhotos, setMemberPhotos] = useState({});
    const [extractedFamilyPhotos, setExtractedFamilyPhotos] = useState([]);

    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    const [loadingFamily, setLoadingFamily] = useState(true);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [loadingExtractedPhotos, setLoadingExtractedPhotos] = useState(true);
    const [isAssigning, setIsAssigning] = useState(false);

    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (loadingAuth || !familyId) {
            if (!loadingAuth && !familyId) {
                setError("Family ID is missing.");
                setLoadingFamily(false); setLoadingMembers(false); setLoadingExtractedPhotos(false);
            }
            return;
        }
        setLoadingFamily(true);
        const fetchFamily = async () => {
            try {
                const familyDocRef = doc(db, 'family', familyId);
                const familyDocSnap = await getDoc(familyDocRef);
                if (familyDocSnap.exists()) {
                    setFamily({ id: familyDocSnap.id, ...familyDocSnap.data() });
                } else { setError("Family not found."); }
            } catch (err) {
                console.error("Error fetching family data:", err);
                setError("Could not load family data. " + err.message);
            } finally { setLoadingFamily(false); }
        };
        fetchFamily();
    }, [familyId, loadingAuth]);

    useEffect(() => {
        if (!family || !family.orgId) {
            if (family === null && !loadingFamily) { setLoadingMembers(false); }
            return;
        }
        setLoadingMembers(true);
        const fetchMembers = async () => {
            try {
                const membersQuery = query(collection(db, 'member'), where('familyId', '==', family.id), where('orgId', '==', family.orgId), orderBy('name'));
                const memberSnapshot = await getDocs(membersQuery);

                // *** MODIFIED: Ensure 'id' is the document ID and filter invalid IDs ***
                const memberList = memberSnapshot.docs
                    .map(d => ({ ...d.data(), id: d.id })) // Document ID 'd.id' now overwrites any 'id' field from d.data()
                    .filter(member => {
                        if (member.id && typeof member.id === 'string' && member.id.trim() !== '') {
                            return true;
                        }
                        console.warn("Filtered out member with invalid document ID:", member);
                        return false;
                    });

                console.log("Fetched and Filtered Members List (using document ID):", memberList);
                setMembers(memberList);

                if (memberList.length > 0) {
                    const photoPromises = memberList.map(async (member) => {
                        if (member.photo && !member.photo.startsWith('http')) {
                            try {
                                const url = await getDownloadURL(ref(storage, member.photo));
                                return { memberId: member.id, url };
                            } catch (photoError) {
                                if (photoError.code !== 'storage/object-not-found') { console.warn(`Could not get photo URL for member ${member.name}: ${member.photo}`, photoError); }
                                return { memberId: member.id, url: DEFAULT_MEMBER_PHOTO_URL };
                            }
                        } else if (member.photo && member.photo.startsWith('http')) {
                            return { memberId: member.id, url: member.photo };
                        }
                        return { memberId: member.id, url: DEFAULT_MEMBER_PHOTO_URL };
                    });
                    const resolvedPhotos = await Promise.all(photoPromises);
                    setMemberPhotos(resolvedPhotos.reduce((acc, curr) => {
                        if (curr.memberId) {
                            acc[curr.memberId] = curr.url;
                        }
                        return acc;
                    }, {}));
                } else {
                    setMemberPhotos({});
                }
            } catch (err) {
                console.error("Error fetching members:", err);
                setError("Could not load family members. " + err.message);
            } finally { setLoadingMembers(false); }
        };
        fetchMembers();
    }, [family, loadingFamily]);

    useEffect(() => {
        if (!family || !family.orgId) {
            if (family === null && !loadingFamily) { setLoadingExtractedPhotos(false); }
            return;
        }
        setLoadingExtractedPhotos(true);
        const photosPath = `${family.orgId}/family/${family.id}/members/`;
        const listRef = ref(storage, photosPath);
        listAll(listRef)
            .then(async (res) => {
                const photoPromises = res.items.map(async (itemRef) => {
                    const downloadUrl = await getDownloadURL(itemRef);
                    return { path: itemRef.fullPath, url: downloadUrl };
                });
                const photosData = await Promise.all(photoPromises);
                setExtractedFamilyPhotos(photosData);
            })
            .catch((err) => {
                if (err.code === 'storage/object-not-found') { setExtractedFamilyPhotos([]); }
                else { console.error("Error listing extracted photos:", err); setError("Could not load extracted photos. " + err.message); }
            })
            .finally(() => { setLoadingExtractedPhotos(false); });
    }, [family, loadingFamily]);

    const handleMemberSelect = (memberId, memberName) => {
        console.log(`handleMemberSelect called for member: ${memberName || 'N/A'}, ID: ${memberId}`);
        if (memberId === null || typeof memberId === 'undefined' || memberId === '') {
            console.error("CRITICAL: handleMemberSelect called with invalid memberId for member:", memberName);
            setError("An internal error occurred: Attempted to select a member with an invalid ID.");
            return;
        }
        setSelectedMemberId(memberId);
        setSuccessMessage(null);
    };

    const handlePhotoSelect = (photoData) => {
        setSelectedPhoto(photoData);
        setSuccessMessage(null);
    };

    const handleAssignPhoto = async () => {
        if (!selectedMemberId || !selectedPhoto) {
            setError("Please select a member and a photo to assign.");
            return;
        }
        setIsAssigning(true); setError(null); setSuccessMessage(null);
        try {
            const memberDocRef = doc(db, 'member', selectedMemberId);
            await updateDoc(memberDocRef, { photo: selectedPhoto.path });
            setMembers(prevMembers => prevMembers.map(m => m.id === selectedMemberId ? { ...m, photo: selectedPhoto.path } : m));
            setMemberPhotos(prev => ({ ...prev, [selectedMemberId]: selectedPhoto.url }));
            setSuccessMessage(`Photo assigned successfully to ${members.find(m => m.id === selectedMemberId)?.name}!`);
        } catch (err) {
            console.error("Error assigning photo:", err);
            setError("Failed to assign photo. " + err.message);
        } finally { setIsAssigning(false); }
    };

    const getDynamicInstruction = () => {
        const selectedMemberName = selectedMemberId ? (members.find(m => m.id === selectedMemberId)?.name || "the selected member") : "";
        if (!selectedMemberId && !selectedPhoto) {
            return "To begin, please select a family member from the list on the left.";
        } else if (selectedMemberId && !selectedPhoto) {
            return `Great! ${selectedMemberName} is selected. Now, choose a photo from the "Available Photos" grid on the right.`;
        } else if (!selectedMemberId && selectedPhoto) {
            return "You've picked a photo. Next, select a family member from the list on the left to assign it to.";
        } else {
            return `All set! Click the "Assign" button below to assign this photo to ${selectedMemberName}.`;
        }
    };

    if (loadingAuth || loadingFamily) {
        return <Container className="text-center mt-5"><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></Container>;
    }

    if (error && !family) {
        return (
            <Container className="mt-3">
                 <Breadcrumb>
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item>
                    <Breadcrumb.Item active>Error</Breadcrumb.Item>
                </Breadcrumb>
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-3 assign-photos-page">
            <Row className="mb-3">
                <Col>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item>
                        {family && <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/family/${familyId}` }}>{family.surname || 'Family'}</Breadcrumb.Item>}
                        <Breadcrumb.Item active>Assign Member Photos</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>

            {family && <h2 className="mb-3">Assign Photos for {family.surname || 'N/A'} Family</h2>}

            <Alert variant="info" className="mb-4">
                <p className="mb-0">{getDynamicInstruction()}</p>
            </Alert>

            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="mb-3">{successMessage}</Alert>}

            <Row>
                <Col md={4} className="mb-3 mb-md-0">
                    <Card className="h-100">
                        <Card.Header as="h4" className="fs-5">Family Members</Card.Header>
                        <Card.Body>
                            {loadingMembers ? (
                                <div className="text-center"><Spinner animation="border" size="sm" /></div>
                            ) : members.length === 0 ? (
                                <Alert variant="info" size="sm" className="mb-0">No members found for this family.</Alert>
                            ) : (
                                <ListGroup variant="flush">
                                    {members.map(member => {
                                        // Key should always be valid now due to filtering
                                        return (
                                            <ListGroup.Item
                                                key={member.id}
                                                action
                                                active={selectedMemberId === member.id}
                                                onClick={() => handleMemberSelect(member.id, member.name)}
                                                className="d-flex justify-content-between align-items-center member-list-item"
                                            >
                                                <div style={{ flexGrow: 1, flexShrink: 1, minWidth: 0, marginRight: '0.5rem' }}>
                                                    {member.name || 'Unnamed Member'}
                                                    {!member.photo && (
                                                        <div className="text-muted small fst-italic" style={{lineHeight: '1.2'}}>
                                                            No Photo Assigned
                                                        </div>
                                                    )}
                                                </div>
                                                <Image
                                                    src={memberPhotos[member.id] || DEFAULT_MEMBER_PHOTO_URL}
                                                    roundedCircle
                                                    className="member-current-photo"
                                                    alt={member.name || 'Member photo'}
                                                    onError={(e) => {
                                                        if (e.target.src !== DEFAULT_MEMBER_PHOTO_URL) {
                                                            e.target.onerror = null;
                                                            e.target.src = DEFAULT_MEMBER_PHOTO_URL;
                                                        }
                                                    }}
                                                />
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={8} className="mb-3">
                     <Card className="h-100">
                        <Card.Header as="h4" className="fs-5">Available Photos</Card.Header>
                        <Card.Body>
                            {loadingExtractedPhotos ? (
                                <div className="text-center"><Spinner animation="border" size="sm" /></div>
                            ) : extractedFamilyPhotos.length === 0 ? (
                                <Alert variant="info" size="sm" className="mb-0">No extracted photos found for this family. If you've recently uploaded a family photo, please allow a few moments for faces to be processed and appear here.</Alert>
                            ) : (
                                <div className="extracted-photos-grid">
                                    {extractedFamilyPhotos.map(photo => (
                                        <Figure
                                            key={photo.path}
                                            className={`extracted-photo-item ${selectedPhoto?.path === photo.path ? 'selected' : ''}`}
                                            onClick={() => handlePhotoSelect(photo)}
                                        >
                                            <Figure.Image
                                                src={photo.url}
                                                alt="Extracted face"
                                                className="img-thumbnail"
                                                onError={(e) => { e.target.closest('.extracted-photo-item').style.display='none'; }}
                                            />
                                        </Figure>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col className="text-center">
                    <Button
                        variant="primary"
                        size="lg"
                        disabled={!selectedMemberId || !selectedPhoto || isAssigning}
                        onClick={handleAssignPhoto}
                    >
                        {isAssigning ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> : null}
                        Assign Selected Photo to Member
                    </Button>
                </Col>
            </Row>
             <Row className="mt-3 mb-3">
                <Col className="text-center">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        Back to Family Details
                    </Button>
                </Col>
            </Row>
        </Container>
    );
}

export default AssignMemberPhotos;
